/**
 * Major-aura icon row (#137) — the REUSABLE curated aura-chip strip shared by
 * the world-nameplate overlay (#137, {@link NameplateAuras}) and each frame of
 * the consolidated unit-frame stack (#138, {@link UnitFrameStack}). One filter +
 * sort + render, two hosts.
 *
 * DISTINCT from the self buff/debuff bar (#139, {@link SelfAuraBar}): that shows
 * (nearly) EVERY one of the viewer's OWN auras; this shows only a CURATED MAJOR
 * subset (CC / big defensives / key debuffs — not every tick-DoT) of ANY unit's
 * auras. The subset is selected by aura CATEGORY (see `auraIcons.ts`), driven by
 * prefs at the call site — never hardcoded here.
 *
 * PURELY COSMETIC / CLIENT-SIDE. Reads the already-wired per-entity `auras`
 * (`WireEntity.auras`) — no sim, no protocol, no hash, no SIM_VERSION. Reuses the
 * shared {@link AuraChip} tile (icon + duration + stacks + border tint) and the
 * `auraIcons.ts` category/kind tables.
 *
 * data-* contract (component + e2e assertions):
 *   • data-testid   — `testId` prop (default "major-aura-row")
 *   • data-count    — number of chips shown (post filter + max cap)
 *   • each chip is an <AuraChip> (data-testid = `chipTestId`, default
 *     "major-aura-chip")
 */

import type { CSSProperties, ReactElement } from "react";
import type { WireAura } from "@pit/protocol";
import {
  DEFAULT_NAMEPLATE_AURA_CATEGORIES,
  type AuraCategory,
} from "../champions/auraIcons.js";
import { DEFAULT_AURA_CHIP_STYLE, type AuraChipStyle } from "./AuraChip.js";
import { AuraStrip } from "./AuraStrip.js";
import {
  compareMajorAuras,
  filterVisibleAuras,
  isAuraInCategory,
} from "./auraFilter.js";

export { compareMajorAuras };

/** Layout treatment for a major-aura row. Every knob is data-driven (#48). */
export interface MajorAuraRowStyle {
  /** Gap between chips, px. */
  readonly chipGapPx: number;
  /** Per-chip visual style (smaller than the self-bar chip by default). */
  readonly chip: AuraChipStyle;
}

/** Default treatment: compact chips (nameplate/frame scale), tight spacing. */
export const DEFAULT_MAJOR_AURA_ROW_STYLE: MajorAuraRowStyle = {
  chipGapPx: 3,
  chip: { ...DEFAULT_AURA_CHIP_STYLE, sizePx: 24 },
};

/** True when this aura's curation category is in the selected "major" set. */
export function isMajorAura(
  aura: WireAura,
  categories: readonly AuraCategory[],
): boolean {
  return isAuraInCategory(aura, categories);
}

/**
 * Filter `auras` to the major subset (by category selection) and return them in
 * canonical display order. Pure — the reducer of the whole row, exposed so tests
 * drive filter + sort directly. Optional `max` caps the row, keeping the
 * highest-priority N after sorting (a crowded nameplate stays readable).
 */
export function majorAuras(
  auras: readonly WireAura[],
  categories: readonly AuraCategory[],
  now: number,
  max?: number,
): readonly WireAura[] {
  return filterVisibleAuras(auras, {
    now,
    categories,
    max,
    sortBy: "category",
  });
}

export interface MajorAuraRowProps {
  readonly auras: readonly WireAura[];
  /** Current sim tick (for each chip's `expiresAtTick − now` countdown). */
  readonly now: number;
  /** Ticks per second (chip countdown). Defaults to the sim rate. */
  readonly tickRate?: number;
  /** Selected "major" categories. Defaults to the shipped nameplate set. */
  readonly categories?: readonly AuraCategory[];
  /** Cap on chips shown (post-sort). Undefined = uncapped. */
  readonly max?: number;
  readonly style?: MajorAuraRowStyle;
  /** Overridable test id (default "major-aura-row") so hosts can namespace. */
  readonly testId?: string;
  /** Per-chip test id (default "major-aura-chip"). */
  readonly chipTestId?: string;
}

export function MajorAuraRow({
  auras,
  now,
  tickRate,
  categories = DEFAULT_NAMEPLATE_AURA_CATEGORIES,
  max,
  style = DEFAULT_MAJOR_AURA_ROW_STYLE,
  testId = "major-aura-row",
  chipTestId = "major-aura-chip",
}: MajorAuraRowProps): ReactElement {
  const shown = majorAuras(auras, categories, now, max);

  const container: CSSProperties = {
    flexDirection: "row",
    gap: style.chipGapPx,
    pointerEvents: "none",
  };

  return (
    <AuraStrip
      auras={shown}
      now={now}
      tickRate={tickRate}
      chipStyle={style.chip}
      style={container}
      testId={testId}
      chipTestId={chipTestId}
    />
  );
}
